import React, { useState, useRef, useEffect } from 'react';
import { HelpCircle, X } from 'lucide-react';

interface HelpTooltipProps {
  content: string;
  title?: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  className?: string;
}

const HelpTooltip: React.FC<HelpTooltipProps> = ({ 
  content, 
  title, 
  position = 'auto',
  className = '' 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [actualPosition, setActualPosition] = useState<'top' | 'bottom' | 'left' | 'right'>('top');
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Calculate optimal position based on viewport boundaries
  useEffect(() => {
    if (isVisible && tooltipRef.current && triggerRef.current) {
      const tooltip = tooltipRef.current;
      const trigger = triggerRef.current;
      const rect = trigger.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();
      
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      
      let newPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';
      
      if (position === 'auto') {
        // For wider tooltips, prefer horizontal positioning when possible
        // Check if there's enough space to the right (preferred for wider tooltips)
        if (rect.right + tooltipRect.width + 12 < viewportWidth) {
          newPosition = 'right';
        }
        // Check if there's enough space to the left
        else if (rect.left - tooltipRect.width - 12 > 0) {
          newPosition = 'left';
        }
        // Check if there's enough space below (prefer bottom over top to avoid going off-screen)
        else if (rect.bottom + tooltipRect.height + 12 < viewportHeight) {
          newPosition = 'bottom';
        }
        // Only use top if we're not too close to the top of the screen
        else if (rect.top > 100 && rect.top - tooltipRect.height - 12 > 0) {
          newPosition = 'top';
        }
        // Fallback to bottom if no space anywhere
        else {
          newPosition = 'bottom';
        }
      } else {
        newPosition = position as 'top' | 'bottom' | 'left' | 'right';
      }
      
      setActualPosition(newPosition);
    }
  }, [isVisible, position]);

  const getPositionClasses = () => {
    // Safety check: if we're at the very top of the page, force bottom positioning
    if (actualPosition === 'top' && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      if (rect.top < 50) { // If trigger is within 50px of top, use bottom instead
        return 'top-full left-1/2 transform -translate-x-1/2 mt-2';
      }
    }
    
    switch (actualPosition) {
      case 'top':
        return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2';
      case 'bottom':
        return 'top-full left-1/2 transform -translate-x-1/2 mt-2';
      case 'left':
        return 'right-full top-1/2 transform -translate-y-1/2 mr-2';
      case 'right':
        return 'left-full top-1/2 transform -translate-y-1/2 ml-2';
      default:
        return 'top-full left-1/2 transform -translate-x-1/2 mt-2'; // Default to bottom for safety
    }
  };

  const getArrowClasses = () => {
    // Safety check: if we're at the very top of the page, force bottom arrow
    if (actualPosition === 'top' && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      if (rect.top < 50) { // If trigger is within 50px of top, use bottom arrow instead
        return 'bottom-full left-1/2 transform -translate-x-1/2 border-b-gray-900 dark:border-b-gray-100';
      }
    }
    
    switch (actualPosition) {
      case 'top':
        return 'top-full left-1/2 transform -translate-x-1/2 border-t-gray-900 dark:border-t-gray-100';
      case 'bottom':
        return 'bottom-full left-1/2 transform -translate-x-1/2 border-b-gray-900 dark:border-b-gray-100';
      case 'left':
        return 'left-full top-1/2 transform -translate-y-1/2 border-l-gray-900 dark:border-l-gray-100';
      case 'right':
        return 'right-full top-1/2 transform -translate-y-1/2 border-r-gray-900 dark:border-r-gray-100';
      default:
        return 'bottom-full left-1/2 transform -translate-x-1/2 border-b-gray-900 dark:border-b-gray-100'; // Default to bottom arrow for safety
    }
  };

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => setIsVisible(!isVisible)}
        className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
        aria-label={title ? `${title} help` : 'More information'}
      >
        <HelpCircle className="h-4 w-4" />
      </button>
      
      {isVisible && (
        <div 
          ref={tooltipRef}
          className={`absolute z-50 ${getPositionClasses()}`}
        >
          <div className="bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-sm rounded-lg shadow-lg p-3 max-w-sm min-w-64">
            {title && (
              <div className="font-semibold mb-1">{title}</div>
            )}
            <div className="whitespace-pre-wrap">{content}</div>
            
            {/* Arrow */}
            <div className={`absolute w-0 h-0 border-4 border-transparent ${getArrowClasses()}`}></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpTooltip;
